import cx from "classnames";

import { Button, Checkbox, Flex, LabelWrapper, Select, Text } from "@theorgchartdev/storybook";
import { FieldInputValuePickerComposite } from "@/appComponents/Blocks/Composites/FieldInputValuePickerComposite/FieldInputValuePickerComposite";
import { appstr } from "@coreapp/utils/appstr";
import { getFeatureAccess } from "@/Utils/Misc";
import { enumFeaturesList } from "@coreapp/constants";

const defaultPickListInfo = {
  isPicklist: false,
  picklistType: "constrained",
  pickValues: []
};

export const FieldPicklistConfiguration = props => {
  const pickInfo = {
    ...defaultPickListInfo,
    ...props.fieldFormat?.pickListOptions
  };

  const handleTogglePicklist = () => {
    const nextIsPicklist = !pickInfo.isPicklist;
    if (nextIsPicklist) {
      props.onEnable();
    }

    const nextPickValues =
      nextIsPicklist && pickInfo.pickValues.length === 0 ? [""] : pickInfo.pickValues;
    props.onChange("pickListOptions", {
      ...pickInfo,
      isPicklist: nextIsPicklist,
      pickValues: nextPickValues
    });
  };

  const handleChangeType = option => {
    props.onChange("pickListOptions", {
      ...pickInfo,
      picklistType: option.value
    });
  };

  const handleValueUpdate =
    index =>
    ({ value }) => {
      const nextValues = [...pickInfo.pickValues];
      nextValues[index] = value;
      props.onChange("pickListOptions", {
        ...pickInfo,
        pickValues: nextValues
      });
    };

  const handleAddValue = () => {
    props.onChange("pickListOptions", {
      ...pickInfo,
      pickValues: [...pickInfo.pickValues, ""]
    });
  };

  const handleRemoveValue = index => () => {
    const nextValues = pickInfo.pickValues.filter((_, i) => i !== index);
    props.onChange("pickListOptions", {
      ...pickInfo,
      pickValues: nextValues
    });
  };

  return (
    <Flex direction={"column"} gap={"12px"}>
      <Text modifier="bold" size="small">
        {appstr("Picklist options")}
      </Text>
      <Checkbox
        id="togglePicklist"
        data-testid="field-picklist-toggle"
        label={appstr("Set picklist")}
        size="large"
        checked={pickInfo.isPicklist}
        onToggle={handleTogglePicklist}
        disabled={!getFeatureAccess(enumFeaturesList.pickLists)}
      />
      <Select
        dataTestId={`${props.dataTestIdPrefix}-picklist-type`}
        disabled={!pickInfo.isPicklist}
        inputOnly={false}
        selectSize="medium"
        value={pickInfo.picklistType}
        label={appstr("Picklist type")}
        listProps={{ onSelect: handleChangeType }}
        options={[
          {
            value: "constrained",
            label: appstr("Constrained")
          },
          {
            value: "unconstrained",
            label: appstr("Unconstrained")
          }
        ]}
      />
      <LabelWrapper label={appstr("Allowed values")}>
        <Flex gap={"8px"} direction={"column"}>
          {pickInfo.pickValues.map((value, index) => (
            <Flex key={index} gap={"8px"} alignItems={"center"}>
              <FieldInputValuePickerComposite
                dataTestIdPrefix={`field-picklist-${index}-`}
                ignorePicklistValues
                field={props.fieldName}
                value={value}
                onChange={handleValueUpdate(index)}
                inputProps={{
                  label: undefined,
                  clearable: value.length > 0,
                  size: "medium",
                  disabled: !pickInfo.isPicklist
                }}
                triggerProps={{
                  className: undefined,
                  size: "large",
                  disabled: !pickInfo.isPicklist
                }}
                menuProps={{
                  position: "top-start",
                  popoverProps: {
                    flipOptions: {
                      mainAxis: false
                    }
                  }
                }}
              />
              <Button
                dataTestId={`field-picklist-${index}-remove`}
                disabled={!pickInfo.isPicklist}
                onClick={handleRemoveValue(index)}
                modifier={"tertiary"}
                iconOnly={true}
                iconProps={{ name: "trash-can", color: "error" }}
              />
            </Flex>
          ))}
        </Flex>
      </LabelWrapper>
      <div>
        <Button
          dataTestId={`${props.dataTestIdPrefix}-picklist-add-value`}
          disabled={!pickInfo.isPicklist}
          onClick={handleAddValue}
          className={cx("oc-special-secondary", "oc-special-secondary--medium")}
          iconProps={{ name: "plus" }}
          modifier={"secondary"}
        >
          Add value
        </Button>
      </div>
    </Flex>
  );
};
